import React, { useState, useMemo } from 'react';
import { Table, Button, Space, Card, Typography, Modal, Form, Input, message, Tag, Checkbox, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ImportOutlined, SearchOutlined, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  getCategories, addCategory, updateCategory, removeCategory,
  isCategoryIdentifierUnique, getCategoryCapCount, getCategoryLinkedCapIds, linkCapToCategory,
  getModules, getCapabilities, TYPE_LABEL, TYPE_COLOR, IDENT_REGEX
} from '../thingModelData';

const { Title, Text } = Typography;

/**
 * IOT类目管理页面
 * 功能：类目 CRUD + 导入能力（穿梭框为类目关联标准能力）
 * 来源：平移 iot-platform /thing-model/category 的 UI/UE
 */
const IotCategory = () => {
  const [categories, setCategories] = useState(() => getCategories());
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // 刷新列表
  const refresh = () => setCategories([...getCategories()]);

  // ===== 类目 CRUD =====
  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };
  const openEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalVisible(true);
  };
  const handleSave = () => {
    form.validateFields().then(values => {
      if (!IDENT_REGEX.test(values.identifier)) { message.warning('标识符格式：字母开头，仅允许字母、数字、下划线'); return; }
      if (!isCategoryIdentifierUnique(values.identifier, editingId || undefined)) { message.warning('标识符已存在，请更换'); return; }
      if (editingId) {
        updateCategory(editingId, values);
        message.success('编辑成功');
      } else {
        addCategory(values);
        message.success('添加成功');
      }
      setModalVisible(false);
      refresh();
    }).catch(() => {});
  };
  const handleDelete = (record) => {
    const count = getCategoryCapCount(record.id);
    Modal.confirm({
      title: '删除确认',
      content: `确定删除类目「${record.name}」？关联的 ${count} 个能力将同步解绑。`,
      okText: '确定',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => { removeCategory(record.id); refresh(); message.success('已删除'); },
    });
  };

  // ===== 导入能力（穿梭框） =====
  const [importVisible, setImportVisible] = useState(false);
  const [importCatId, setImportCatId] = useState(null);
  const [importSelected, setImportSelected] = useState([]);    // 右侧已选
  const [importChecked, setImportChecked] = useState([]);       // 左侧勾选
  const [importSearch, setImportSearch] = useState('');

  const allCaps = getCapabilities();
  const allMods = getModules();

  const linkedIds = useMemo(() => importCatId ? getCategoryLinkedCapIds(importCatId) : [], [importCatId]);
  const excludedIds = useMemo(() => new Set([...linkedIds, ...importSelected]), [linkedIds, importSelected]);

  // 左侧可选能力（按模块分组）
  const availableByModule = useMemo(() => {
    const available = allCaps.filter(c => !excludedIds.has(c.id));
    const groups = [];
    const usedMods = new Set();
    allMods.forEach(mod => {
      const caps = available.filter(c => c.moduleId === mod.id);
      if (caps.length > 0) { groups.push({ mod, caps }); usedMods.add(mod.id); }
    });
    const orphan = available.filter(c => !usedMods.has(c.moduleId));
    if (orphan.length > 0) groups.push({ mod: { id: '_other', name: '其他', identifier: '' }, caps: orphan });
    return groups;
  }, [allCaps, allMods, excludedIds]);

  // 左侧过滤搜索
  const filterCap = (cap) => {
    if (!importSearch) return true;
    const kw = importSearch.toLowerCase();
    return cap.name.toLowerCase().includes(kw) || cap.identifier.toLowerCase().includes(kw);
  };

  const getVisibleCaps = (modEntry) => {
    return importSearch ? modEntry.caps.filter(filterCap) : modEntry.caps;
  };

  // Module check state
  const isModuleAllChecked = (modEntry) => {
    const visible = getVisibleCaps(modEntry);
    return visible.length > 0 && visible.every(c => importChecked.includes(c.id));
  };
  const isModuleIndeterminate = (modEntry) => {
    const visible = getVisibleCaps(modEntry);
    const checked = visible.filter(c => importChecked.includes(c.id));
    return checked.length > 0 && checked.length < visible.length;
  };
  const toggleModuleCheck = (modEntry) => {
    const visible = getVisibleCaps(modEntry);
    if (isModuleAllChecked(modEntry)) {
      setImportChecked(prev => prev.filter(id => !visible.some(c => c.id === id)));
    } else {
      setImportChecked(prev => [...new Set([...prev, ...visible.map(c => c.id)])]);
    }
  };

  // 右侧已选（按模块分组）
  const selectedByModule = useMemo(() => {
    const caps = importSelected.map(id => allCaps.find(c => c.id === id)).filter(Boolean);
    const groups = [];
    allMods.forEach(mod => {
      const mcaps = caps.filter(c => c.moduleId === mod.id);
      if (mcaps.length > 0) groups.push({ mod, caps: mcaps });
    });
    const orphan = caps.filter(c => !allMods.some(m => m.id === c.moduleId));
    if (orphan.length > 0) groups.push({ mod: { id: '_other', name: '其他', identifier: '' }, caps: orphan });
    return groups;
  }, [importSelected, allCaps, allMods]);

  const openImport = (categoryId) => {
    setImportCatId(categoryId);
    setImportSelected([]);
    setImportChecked([]);
    setImportSearch('');
    setImportVisible(true);
  };

  const transferRight = () => {
    setImportSelected(prev => [...new Set([...prev, ...importChecked])]);
    setImportChecked([]);
  };
  const transferLeft = () => { setImportSelected([]); };
  const handleImportConfirm = () => {
    importSelected.forEach(capId => linkCapToCategory(importCatId, capId));
    message.success(`已导入 ${importSelected.length} 个能力`);
    setImportVisible(false);
    refresh();
  };

  const typeTag = (capType) => {
    const label = TYPE_LABEL[capType];
    const clr = TYPE_COLOR[capType] || { bg: '#f0f0f0', color: '#999' };
    return <Tag style={{ background: clr.bg, color: clr.color, border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>{label}</Tag>;
  };

  // ===== 表格列 =====
  const columns = [
    {
      title: '类目名称', dataIndex: 'name', key: 'name', minWidth: 140,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: '标识符', dataIndex: 'identifier', key: 'identifier', width: 180,
      render: (t) => <Text style={{ fontFamily: 'monospace', fontSize: 13, color: '#666' }}>{t}</Text>,
    },
    {
      title: '标准能力', dataIndex: 'id', key: 'capCount', width: 120, align: 'center',
      render: (id) => <Button type="link" size="small" onClick={() => openImport(id)}>{getCategoryCapCount(id)} 个</Button>,
    },
    {
      title: '描述', dataIndex: 'descr', key: 'descr', minWidth: 160, ellipsis: true,
      render: (t) => t ? <Text style={{ fontSize: 13, color: '#666' }}>{t}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: '操作', key: 'action', width: 260,
      render: (_, record) => (
        <Space size={0} split={<span style={{ color: '#d9d9d9', margin: '0 8px' }}>|</span>}>
          <Button type="link" size="small" icon={<ImportOutlined />} onClick={() => openImport(record.id)}>导入能力</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>设备类目</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加类目</Button>
        </div>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          locale={{ emptyText: <Empty description="暂无设备类目，请点击上方按钮添加" /> }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Card>

      {/* 添加/编辑类目弹窗 */}
      <Modal
        title={editingId ? '编辑类目' : '添加类目'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        okText="确定"
        cancelText="取消"
        destroyOnClose
        width={480}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="类目名称" rules={[{ required: true, message: '请输入类目名称' }]}>
            <Input placeholder="请输入类目名称（最多40字符）" maxLength={40} />
          </Form.Item>
          <Form.Item
            name="identifier"
            label="标识符"
            rules={[
              { required: true, message: '请输入标识符' },
              { pattern: IDENT_REGEX, message: '字母开头，仅允许字母、数字、下划线' },
            ]}
            extra={<span style={{ fontSize: 12, color: '#999' }}>字母开头，仅允许字母、数字、下划线</span>}
          >
            <Input placeholder="例如 IpcCamera" maxLength={40} />
          </Form.Item>
          <Form.Item name="descr" label="描述">
            <Input.TextArea placeholder="请输入描述（最多200字符）" maxLength={200} rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入能力穿梭框 */}
      <Modal
        title="导入能力"
        open={importVisible}
        onCancel={() => setImportVisible(false)}
        width={960}
        footer={null}
        style={{ top: 24 }}
        destroyOnClose
      >
        <div style={{ display: 'flex', height: 420, gap: 0 }}>
          {/* 左侧：可选能力 */}
          <div style={{ flex: 1, border: '1px solid #f0f0f0', borderRadius: 6, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>可选能力</Text>
              {importChecked.length > 0 && <Text type="secondary" style={{ fontSize: 12 }}>已选 <span style={{ color: '#1677ff', fontWeight: 600 }}>{importChecked.length}</span> 个</Text>}
            </div>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
              <Input
                size="small"
                placeholder="搜索能力名称或标识"
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                allowClear
                value={importSearch}
                onChange={e => setImportSearch(e.target.value)}
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
              {availableByModule.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无可导入的能力</div>
              ) : (
                availableByModule.map(modEntry => (
                  <div key={modEntry.mod.id} style={{ marginBottom: 4 }}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', cursor: 'pointer', borderRadius: 4, gap: 6 }}
                    >
                      <Checkbox
                        checked={isModuleAllChecked(modEntry)}
                        indeterminate={isModuleIndeterminate(modEntry)}
                        onChange={() => toggleModuleCheck(modEntry)}
                      />
                      <Text style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{modEntry.mod.name}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{modEntry.caps.length} 个能力</Text>
                    </div>
                    <div style={{ paddingLeft: 30 }}>
                      {getVisibleCaps(modEntry).map(cap => (
                        <label
                          key={cap.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                            borderRadius: 4, cursor: 'pointer', marginBottom: 2,
                            background: importChecked.includes(cap.id) ? '#e6f4ff' : 'transparent',
                          }}
                        >
                          <Checkbox
                            checked={importChecked.includes(cap.id)}
                            onChange={() => setImportChecked(prev =>
                              prev.includes(cap.id) ? prev.filter(id => id !== cap.id) : [...prev, cap.id]
                            )}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {typeTag(cap.capType)}
                              <Text style={{ fontSize: 13 }} ellipsis>{cap.name}</Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 11 }}>{cap.identifier}</Text>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 中间操作按钮 */}
          <div style={{ width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <Button size="small" disabled={importChecked.length === 0} onClick={transferRight} icon={<ArrowRightOutlined />} />
            <Button size="small" disabled={importSelected.length === 0} onClick={transferLeft} icon={<ArrowLeftOutlined />} />
          </div>

          {/* 右侧：待导入 */}
          <div style={{ flex: 1, border: '1px solid #f0f0f0', borderRadius: 6, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>待导入</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{importSelected.length} 项</Text>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
              {importSelected.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>请从左侧勾选能力后点击 → 添加</div>
              ) : (
                selectedByModule.map(modEntry => (
                  <div key={modEntry.mod.id} style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', gap: 6 }}>
                      <Text style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{modEntry.mod.name}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>{modEntry.caps.length} 个能力</Text>
                    </div>
                    <div style={{ paddingLeft: 16 }}>
                      {modEntry.caps.map(cap => (
                        <div key={cap.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                            borderRadius: 4, background: '#e6f4ff', marginBottom: 2,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {typeTag(cap.capType)}
                              <Text style={{ fontSize: 13 }} ellipsis>{cap.name}</Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 11 }}>{cap.identifier}</Text>
                          </div>
                          <Button type="link" size="small" danger
                            onClick={() => setImportSelected(prev => prev.filter(id => id !== cap.id))}
                          >移除</Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={() => setImportVisible(false)}>取消</Button>
          <Button type="primary" disabled={importSelected.length === 0} onClick={handleImportConfirm}>
            导入（{importSelected.length}）
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default IotCategory;
